import { logger } from '@hatchd/utils';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { UsersApi } from 'api/client';
import { ReactComponent as SaveIcon } from 'assets/icons/icon-save.svg';
import {
  Form as FormOuter,
  GlobalError,
  PasswordInfo,
} from 'components/auth-form/auth-form.styles';
import BackLink from 'components/back-link';
import Button from 'components/button';
import Checkbox from 'components/checkbox';
import DeleteAccountButton from 'components/delete-account-button';
import EditToggle, { EditToggleHandles } from 'components/edit-toggle';
import { EditToggleGroup } from 'components/edit-toggle/edit-toggle.styles';
import { H4, H5 } from 'components/headings';
import Layout from 'components/layout';
import SEO from 'components/seo';
import SwitchTheme from 'components/switch-theme';
import TextInput from 'components/text-input';
import ContentSection from 'components/ui/content-section';
import { TextButton } from 'components/ui/text-button';
import useAuth from 'hooks/use-auth';
import { useUserProfileAPI } from 'hooks/use-user-profile';
import { GetStaticProps, NextPage } from 'next';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useEffectOnce } from 'react-use';
import AnalyticsService, { ScreenNames } from 'services/analytics';
import { backgroundImages, passwordRegexp, settings } from 'utils/config';
import { resolveApiError } from 'utils/helpers';
import { protectRoute } from 'utils/protect-route';
import APP_ROUTES from 'utils/routes';
import statusMessages from 'utils/status-messages';
import * as yup from 'yup';

const PAGE_TITLE = 'Account Settings';

// Name Form ---------------------------------------------------------------------------------------
type NameFormData = {
  displayName: string;
  nameGlobalError?: string;
};

const nameSchema = yup.object().shape({
  displayName: yup
    .string()
    .required('Please provide a name.')
    .min(
      settings.nameMinLength,
      `Name must be at least ${settings.nameMinLength} characters.`
    ),
});

// Email Form --------------------------------------------------------------------------------------
type EmailFormData = {
  email: string;
  emailGlobalError?: string;
};

const emailSchema = yup.object().shape({
  email: yup
    .string()
    .email(`Invalid email.`)
    .required('Please provide your email.'),
});

// Password Form -----------------------------------------------------------------------------------
type PasswordFormData = {
  password: string;
  newPassword: string;
  confirmPassword: string;
  passwordGlobalError?: string;
};

const passwordSchema = yup.object().shape({
  password: yup.string().required('Please provide your old password.'),
  newPassword: yup
    .string()
    .required('Please provide a new password.')
    .matches(
      passwordRegexp,
      `Password must contain at least ${settings.passwordMinLength} characters, upper and lowercase letters and at least 1 number.`
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), undefined], 'New passwords must match.'),
});

const Settings: NextPage = () => {
  const { user, token, logout, reloadUser } = useAuth();
  const UserApi = new UsersApi({
    apiKey: token ?? '',
  });

  // Set up name form ------------------------------------------------------------------------------
  const {
    register: registerName,
    handleSubmit: handleNameSubmit,
    errors: nameErrors,
    formState: nameFormState,
    setError: setNameError,
  } = useForm<NameFormData>({
    defaultValues: {
      displayName: user?.displayName ?? '',
    },
    resolver: yupResolver(nameSchema),
  });

  const { isSubmitting: isNameFormSubmitting, touched: nameFormTouched } =
    nameFormState;

  const nameToggleRef = useRef<EditToggleHandles>(null);

  const onNameSubmit = handleNameSubmit(
    async ({ nameGlobalError, ...data }) => {
      try {
        await UserApi.updateUser({
          displayName: data.displayName,
        });
        reloadUser();
        nameToggleRef.current?.toggle();
        toast.success(statusMessages.settings.nameUpdateSuccess);
      } catch (error) {
        logger.error('Error updating name', error);
        toast.error(statusMessages.settings.nameUpdateError);
        resolveApiError(
          error,
          setNameError,
          'nameGlobalError',
          'Error updating name'
        );
      }
    }
  );

  // Set up email form -----------------------------------------------------------------------------
  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    errors: emailErrors,
    formState: emailFormState,
    setError: setEmailError,
  } = useForm<EmailFormData>({
    defaultValues: {
      email: user?.email ?? '',
    },
    resolver: yupResolver(emailSchema),
  });

  const { isSubmitting: isEmailFormSubmitting, touched: emailFormTouched } =
    emailFormState;

  const onEmailSubmit = handleEmailSubmit(
    async ({ emailGlobalError, ...data }) => {
      try {
        await UserApi.updateUser({
          email: data.email,
        });
        toast.success(statusMessages.settings.emailUpdateSuccess);
        //? User will be logged out immediately if the session is too old.
        reloadUser();
      } catch (error) {
        logger.error('Error updating email', error);
        resolveApiError(
          error,
          setEmailError,
          'emailGlobalError',
          'Error updating email'
        );
      }
    }
  );

  // Set up password form --------------------------------------------------------------------------
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    errors: passwordErrors,
    formState: passwordFormState,
    setError: setPasswordError,
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
  });

  const {
    isSubmitting: isPasswordFormSubmitting,
    touched: passwordFormTouched,
  } = passwordFormState;

  const onPasswordSubmit = handlePasswordSubmit(
    async ({ passwordGlobalError, ...data }) => {
      const firebase = (await import('services/firebase/client')).default;

      const credential = firebase.auth.EmailAuthProvider.credential(
        user?.email ?? '',
        data.password
      );

      let validCredential: firebase.default.auth.UserCredential | undefined;
      try {
        validCredential = await firebase
          .auth()
          .currentUser?.reauthenticateWithCredential(credential);
      } catch (error) {
        logger.error('Re-authentication failed', error);

        // override the default error messages for common errors.
        switch (error?.code) {
          case 'auth/wrong-password':
            // If the user used an invalid "old" password, we just show the individual error message
            // instead of the global one.
            error.message = 'The old password is invalid.';
            setPasswordError('password', {
              type: 'manual',
              message: error.message,
            });
            return;
          default:
            break;
        }

        setPasswordError('passwordGlobalError', {
          type: 'manual',
          message: error.message,
        });
      }

      if (validCredential) {
        try {
          await UserApi.updateUser({
            password: data.newPassword,
          });

          toast.success(statusMessages.settings.passwordUpdateSuccess);

          //? User will be logged out immediately if the session is too old.
          reloadUser();
        } catch (error) {
          logger.error('Error updating password', error);
          resolveApiError(
            error,
            setPasswordError,
            'passwordGlobalError',
            'Error updating password'
          );
        }
      }
    }
  );

  useEffectOnce(() => {
    AnalyticsService.trackScreen(ScreenNames.settings);
  });

  // Set up communication preference -----------------------------------------------------------------
  const { useUserProfile, useUpdateUserProfile } = useUserProfileAPI();
  const { data: userProfile, isLoading: userProfileLoading } = useUserProfile();
  const { mutate: updateUserProfile, isLoading: updateUserProfileLoading } =
    useUpdateUserProfile();
  const allowCommunications = userProfile?.data.allowCommunications || false;

  const handleCommunicationChange = () => {
    updateUserProfile({ allowCommunications: !allowCommunications });
  };

  return (
    <Layout mainBackground={backgroundImages.hero}>
      <SEO title={PAGE_TITLE} />
      <ContentSection>
        <FormOuter as='div'>
          <BackLink href={APP_ROUTES.account}>Back to Dashboard</BackLink>
          <H4 as='h1'>{PAGE_TITLE}</H4>
          <SwitchTheme to='light'>
            <EditToggleGroup>
              <EditToggle
                name='Name'
                value={user?.displayName || ''}
                openTitle={<H5 as='h2'>Update name</H5>}
                ref={nameToggleRef}
              >
                <form noValidate onSubmit={onNameSubmit}>
                  <div role='alert'>
                    <ErrorMessage
                      errors={nameErrors}
                      name='nameGlobalError'
                      as={GlobalError}
                    />
                  </div>
                  <TextInput
                    label='Name'
                    type='text'
                    id='displayName'
                    name='displayName'
                    autoComplete='name'
                    required
                    ref={registerName}
                    aria-invalid={
                      nameFormTouched.displayName && !!nameErrors.displayName
                    }
                    disabled={isNameFormSubmitting}
                    errors={nameErrors}
                  />
                  <Button
                    type='submit'
                    loading={isNameFormSubmitting}
                    variant='dark'
                  >
                    <SaveIcon aria-hidden />
                    Save name
                  </Button>
                </form>
              </EditToggle>
              <EditToggle
                name='Email'
                value={user?.email || ''}
                openTitle={<H5 as='h2'>Update email</H5>}
              >
                <form noValidate onSubmit={onEmailSubmit}>
                  <div role='alert'>
                    <ErrorMessage
                      errors={emailErrors}
                      name='emailGlobalError'
                      as={GlobalError}
                    />
                  </div>
                  <TextInput
                    label='Email'
                    id='email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    required
                    ref={registerEmail}
                    aria-invalid={emailFormTouched.email && !!emailErrors.email}
                    disabled={isEmailFormSubmitting}
                    errors={emailErrors}
                  />
                  <PasswordInfo id='email-info' color='redDark'>
                    You will need to log in again with your new email address
                    after saving.
                  </PasswordInfo>
                  <Button
                    type='submit'
                    loading={isEmailFormSubmitting}
                    variant='dark'
                    aria-describedby='email-info'
                  >
                    <SaveIcon aria-hidden />
                    Save email
                  </Button>
                </form>
              </EditToggle>
              <EditToggle
                name='Password'
                value='********'
                openTitle={<H5 as='h2'>Update password</H5>}
              >
                <form noValidate onSubmit={onPasswordSubmit}>
                  <div role='alert'>
                    <ErrorMessage
                      errors={passwordErrors}
                      name='passwordGlobalError'
                      as={GlobalError}
                    />
                  </div>
                  <TextInput
                    label='Current password'
                    type='password'
                    id='password'
                    name='password'
                    autoComplete='current-password'
                    required
                    ref={registerPassword}
                    aria-invalid={
                      passwordFormTouched.password && !!passwordErrors.password
                    }
                    disabled={isPasswordFormSubmitting}
                    errors={passwordErrors}
                  />
                  <TextInput
                    label='New password'
                    type='password'
                    id='newPassword'
                    name='newPassword'
                    required
                    autoComplete='new-password'
                    ref={registerPassword}
                    aria-invalid={
                      passwordFormTouched.newPassword &&
                      !!passwordErrors.newPassword
                    }
                    aria-describedby='password-constraints'
                    disabled={isPasswordFormSubmitting}
                    errors={passwordErrors}
                  />
                  <TextInput
                    label='Confirm new password'
                    type='password'
                    id='confirmPassword'
                    name='confirmPassword'
                    required
                    autoComplete='new-password'
                    ref={registerPassword}
                    aria-invalid={
                      passwordFormTouched.confirmPassword &&
                      !!passwordErrors.confirmPassword
                    }
                    aria-describedby='password-constraints'
                    disabled={isPasswordFormSubmitting}
                    errors={passwordErrors}
                  />
                  <PasswordInfo id='password-constraints'>
                    <em>
                      {settings.passwordMinLength} or more characters with upper
                      and lower case letters, numbers.
                    </em>
                  </PasswordInfo>
                  <PasswordInfo id='password-info' color='redDark'>
                    You will need to log in again with your new password after
                    saving.
                  </PasswordInfo>
                  <Button
                    type='submit'
                    loading={isPasswordFormSubmitting}
                    variant='dark'
                    aria-describedby='password-info'
                  >
                    <SaveIcon aria-hidden />
                    Save password
                  </Button>
                </form>
              </EditToggle>
              <Checkbox
                bold
                id='allowCommunications'
                label='I would like to receive further bushfire preparedness information from DFES by email.'
                name='allowCommunications'
                align='top'
                slim
                checked={allowCommunications}
                onChange={handleCommunicationChange}
                disabled={userProfileLoading || updateUserProfileLoading}
              />
            </EditToggleGroup>
          </SwitchTheme>
          <TextButton onClick={logout.bind(this, '/')}>Logout</TextButton>
          <DeleteAccountButton />
        </FormOuter>
      </ContentSection>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};

export default protectRoute(Settings);
